// noinspection JSUnresolvedReference

import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import React, {useContext, useEffect, useRef, useState} from "react";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {GlobalContext} from "../../../../../utils/context/GlobalContextProvider.jsx";
import {useTranslation} from "react-i18next";
import {chartColors} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import SeeMore from "../../../../../components/SeeMore.jsx";
import {useRenderCurrencyValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import {getBackgroundColors, getBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import {Chart} from "chart.js";
import DataGrid2 from "../../../../../components/datagrid/DataGrid2.jsx";

export default function CouponPerformancePanel({project}) {

    let {t} = useTranslation();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    let {getAuthorityConfiguration} = useContext(GlobalContext);
    let renderCurrencyValue = useRenderCurrencyValue();

    const couponGraphCanvas = useRef(null);

    let [data, setData] = useState(null);

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, textColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    let [userSettings] = useUserSettings();

    useEffect(() => {
        if (!project) {
            return;
        }

        setData(null);
        setCouponDataGridSource(null);

        let authorityConfiguration = getAuthorityConfiguration();

        new ErpCalculationApi(authorityConfiguration)
            .couponPerformance({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            })
            .then(setData)
            .catch(e => et(e).then((message) => { if (message) toast.error(message); }));

    }, [project?.id, since, until, centresAllowListed, domainsAllowListed]);

    let [couponDataGridSource, setCouponDataGridSource] = useState(null);

    useEffect(() => {
        let chartData = {
            labels: Object.values(data?.coupons ?? []).map((coupon) => {
                return coupon.code + ' (' + (userSettings?.isMarketing ? coupon.amountGross : coupon.amountNet) + 'x)';
            }),
            datasets: [{
                label: t('coupons.coupon-use-amount'),
                data: Object.values(data?.coupons ?? []).map((coupon) => {
                    return userSettings?.isMarketing ? coupon.amountGross : coupon.amountNet;
                }),
                backgroundColor: Object.values(data?.coupons ?? []).map((coupon, index) => {
                    return getBackgroundColors()[index];
                }),
                hoverBackgroundColor: Object.values(data?.coupons ?? []).map((coupon, index) => {
                    return getBackgroundHoverColors()[index];
                }),
            }],
        };

        let chart = new Chart(
             couponGraphCanvas.current,
             {
                 type: 'bar',
                 data: chartData,
                 options: {
                     indexAxis: 'y',
                     maintainAspectRatio: false,
                     animation: false,
                     scales: {
                         y: {
                             border: {
                                 display: false,
                             },
                             ticks: {
                                 color: darkMode ? textColor.dark : textColor.light,
                             },
                             grid: {
                                 display: false,
                             },
                         },
                         x: {
                             border: {
                                 display: false,
                             },
                             ticks: {
                                 padding: 10,
                                 align: 'center',
                                 color: darkMode ? textColor.dark : textColor.light,
                             },
                             grid: {
                                 color: darkMode ? gridColor.dark : gridColor.light,
                             },
                         },
                     },
                     plugins: {
                         legend: {
                             display: false,
                         },
                         tooltip: {
                             enabled: false,
                         }
                     }
                 }
             }
         );

         return () => {
             chart.destroy();
         };

    }, [data, userSettings]);

    return [
        <LoadingComponent
            key={'cpo1'}
            loading={!data || !userSettings}
            className="comp__roundedBox flex flex-col self-start justify-between col-span-12 row-span-2 xl:col-span-7 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
        >
            <div
                className={'px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center'}>
                <h2 className={'font-semibold text-slate-800 dark:text-slate-100'}>
                    {t('coupons.coupon-use-amount')}
                </h2>
            </div>
            <div className={'p-2'}>
                <div
                    style={{
                        height: (data?.coupons && data?.coupons.length > 0)
                            ? (35 + (data?.coupons.length * 24)) + 'px'
                            : '200px'
                    }}
                >
                    <canvas ref={couponGraphCanvas}></canvas>
                </div>
            </div>
        </LoadingComponent>,
        <LoadingComponent
            key={'cpo2'}
            loading={!data}
            className="comp__roundedBox flex flex-col justify-between col-span-12 xl:col-span-5 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
        >
            <header
                className={'px-5 py-4 border-b border-gray-100 dark:border-gray-700/60'}>
                <h2 className={'font-semibold text-gray-800 dark:text-gray-100'}>
                    {t('coupons.coupon-usage')}
                </h2>
            </header>
            <div className={'p-3'}>
                <div className="chart-container relative">
                    <SeeMore heightClass={'h-52'} active={(data?.coupons ?? []).length >= 5}>
                        <DataGrid2
                            columns={[
                                {
                                    name: 'code',
                                    header: t('orders.coupon'),
                                    sortable: false,
                                    defaultFlex: 2,
                                },
                                {
                                    name: 'amount',
                                    header: t('orders.amount'),
                                    sortable: false,
                                    textAlign: 'end',
                                    defaultFlex: 1,
                                    render: row => {
                                        return userSettings?.isMarketing ? row.data.amountGross : row.data.amountNet;
                                    }
                                },
                                {
                                    name: 'turnover',
                                    header: t('orders.turnover'),
                                    sortable: false,
                                    textAlign: 'end',
                                    defaultFlex: 2,
                                    render: row => {
                                        return renderCurrencyValue(
                                            userSettings?.isMarketing
                                                ? row.data.turnoverGross
                                                : row.data.turnoverNet,
                                        );
                                    }
                                },
                            ]}
                            dataSource={(data?.coupons ?? [])}
                        />
                    </SeeMore>
                </div>
            </div>
        </LoadingComponent>,

    ];
}